import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createStripeAccount,
  retrieveDetailsStripeAccount,
  createStripeLoginLink,
} from "../../redux/reducers/user";
import { getProfileData } from "../../redux/reducers/auth";
import { ToastContainer, toast } from "react-toastify";
import { AlertCircle, CheckCircle, XCircle } from "lucide-react";
import "./Profile.css";

const Profile = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { err, translationState } = useSelector((state) => state.user);
  const { userLanguage } = useSelector((state) => state.auth);
  const toastTranslation =
    translationState?.[0]?.translations?.toast?.[userLanguage];
  const { user } = useSelector((state) => state.auth);

  const [stripeLoading, setStripeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStripeURL, setIsLoadingStripeURL] = useState(false);

  useEffect(() => {
    if (user?.stripe_account_id) {
      getDetailsStripeAccount();
    } else {
      setIsLoading(false);
    }
  }, []);

  const getDetailsStripeAccount = async () => {
    try {
      const response = await dispatch(
        retrieveDetailsStripeAccount(user.stripe_account_id)
      ).unwrap();
      setStripeAccount(response?.account);
    } catch (err) {
      setError(
        err?.response?.data?.error || err?.message || "Something went wrong."
      );
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const makeStripeAccount = async () => {
    setStripeLoading(true);
    setError(null);

    try {
      if (!user?.id) {
        throw new Error("User ID is missing.");
      }

      const response = await dispatch(createStripeAccount(user.id)).unwrap();
      if (response?.url) {
        await dispatch(getProfileData());
        window.location.href = response.url;
      } else {
        throw new Error("Failed to get Stripe URL.");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error || err?.message || "Something went wrong.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setStripeLoading(false);
    }
  };

  const getRequirementsStatus = () => {
    if (!stripeAccount?.requirements) return null;

    const { currently_due, eventually_due, past_due } =
      stripeAccount.requirements;
    const allRequirements = [
      ...new Set([...currently_due, ...eventually_due, ...past_due]),
    ];

    return allRequirements.map((req) => ({
      requirement: req
        .split(".")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      isPastDue: past_due.includes(req),
    }));
  };

  const loginStripeAccount = () => {
    setIsLoadingStripeURL(true);
    dispatch(createStripeLoginLink(user.stripe_account_id))
      .unwrap()
      .then((response) => {
        setIsLoadingStripeURL(false);
        if (response?.url) {
          window.location.href = response.url;
        } else {
          throw new Error("Failed to login to Stripe account.");
        }
      })
      .catch((err) => {
        setIsLoadingStripeURL(false);
        const errorMessage =
          err?.response?.data?.error || err?.message || "Something went wrong.";
        setError(errorMessage);
        toast.error(errorMessage);
      });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <i
          className="fas fa-spinner fa-pulse fa-2x"
          style={{ color: "green" }}
        ></i>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col stripe-main-container">
      <ToastContainer autoClose={20000} />

      {!user?.stripe_account_id ? (
        <div className="bg-[#F6F6F6] rounded-lg shadow-md p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold">Configure Payout</h2>
          </div>
          <div>
            <p className="mb-4">
              To receive payments, you need to connect your Stripe account.
            </p>
            <button
              onClick={makeStripeAccount}
              disabled={stripeLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {stripeLoading ? "Connecting..." : "Connect Stripe Account"}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold">Stripe Account Details</h2>
          </div>

          {stripeAccount ? (
            <div className="space-y-6">
              {/* Account Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    {stripeAccount.charges_enabled ? (
                      <CheckCircle className="text-green-500 mr-2" />
                    ) : (
                      <AlertCircle className="text-yellow-500 mr-2" />
                    )}
                    <h3 className="font-medium">Account Status</h3>
                  </div>
                  <p
                    className={`${
                      stripeAccount.charges_enabled
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {stripeAccount.charges_enabled
                      ? "Active"
                      : "Pending Activation"}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    {stripeAccount.payouts_enabled ? (
                      <CheckCircle className="text-green-500 mr-2" />
                    ) : (
                      <AlertCircle className="text-yellow-500 mr-2" />
                    )}
                    <h3 className="font-medium">Payouts Status</h3>
                  </div>
                  <p
                    className={`${
                      stripeAccount.payouts_enabled
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {stripeAccount.payouts_enabled ? "Enabled" : "Disabled"}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <h3 className="font-medium">Account Type</h3>
                  </div>
                  <p className="text-gray-600 capitalize">
                    {stripeAccount.type?.toUpperCase()} Account
                  </p>
                </div>
              </div>

              {/* Requirements Section */}
              {getRequirementsStatus()?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-medium mb-3">Required Actions</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="space-y-2">
                      {getRequirementsStatus().map((req, index) => (
                        <li
                          key={index}
                          className="flex items-center"
                          style={{ listStyleType: "none" }}
                        >
                          {req.isPastDue ? (
                            <XCircle className="text-red-500 mr-2" size={16} />
                          ) : (
                            <AlertCircle
                              className="text-yellow-500 mr-2"
                              size={16}
                            />
                          )}
                          <span
                            className={`${
                              req.isPastDue ? "text-red-600" : "text-yellow-600"
                            }`}
                          >
                            {req.requirement}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Payout Schedule */}
              <div className="mt-6">
                <h3 className="font-medium mb-3">Payout Settings</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-600">
                    Schedule:{" "}
                    {stripeAccount.settings?.payouts?.schedule?.interval ===
                    "manual"
                      ? "Manual"
                      : "Automatic"}
                  </p>
                  <p className="text-gray-600">
                    Delay:{" "}
                    {stripeAccount.settings?.payouts?.schedule?.delay_days} days
                  </p>
                </div>
              </div>

              {/* Business Profile */}
              {stripeAccount.business_profile && (
                <div className="mt-6">
                  <h3 className="font-medium mb-3">Business Profile</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600">
                      Business Name:{" "}
                      {stripeAccount.business_profile.name || "Not provided"}
                    </p>
                    <p className="text-gray-600">
                      Website:{" "}
                      {stripeAccount.business_profile.url || "Not provided"}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-6 mb-3">
                <button
                  onClick={() => loginStripeAccount()}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  {isLoadingStripeURL ? "Loading..." : "Login to Stripe"}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <XCircle className="text-red-500 mr-2" />
                <p className="text-red-500">
                  {error || "Unable to load Stripe account details"}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Profile;
